import * as React from 'react'
import {Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, Alert} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import {sectionCardSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, ERROR} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

import {int} from '../format'
import {POLICY} from '../data/sopCopy'
import {ARROW} from './state/packingModel'
import KeyValue from '../components/KeyValue'

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}
const bulletSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.55}

const dangerBtnSx = {...primaryBtnSx, bgcolor: ERROR, '&:hover': {bgcolor: ERROR, filter: 'brightness(0.9)'}}

const TITLE = {add: 'Add a sub-category', rename: 'Rename a sub-category', remove: 'Remove a sub-category'}

/** Never a naive `+ 's'` — matches the module's own convention (see SopPage.js's `levelCoverage`, MergeDrawer.js's `extraCodesNotice`). */
function plural(n, one, many) {
  return n === 1 ? one : many
}

/** `cat` as typed, or the trimmed "new category" name when `__new` is picked — the add-mode Ready gate's own "resolved category". */
export function resolveAddCategory(cat, newCatName) {
  return cat === '__new' ? (newCatName || '').trim() : (cat || '')
}

/**
 * Case-insensitive sibling clash within `cat`: does any OTHER row (by `key`,
 * so a rename can exclude its own current row) already carry this name?
 * Shared by both add mode (no `excludeKey`) and rename mode (`row.key`) so
 * the two can never apply a different notion of "already exists".
 */
export function hasSubcatClash(rows, cat, name, excludeKey) {
  const needle = (name || '').trim().toLowerCase()
  return (rows || []).some((r) => r.cat === cat && r.key !== excludeKey && r.sub.toLowerCase() === needle)
}

/**
 * Every OTHER sub-category as a removal destination, plus a synthetic
 * General > Uncategorised appended only when one doesn't already exist
 * among them — guarded so a row that IS already General > Uncategorised
 * never offers itself as its own destination.
 */
export function buildDestinationOptions(rows, row) {
  if (!row) return []
  const others = (rows || [])
    .filter((r) => r.key !== row.key)
    .map((r) => ({cat: r.cat, sub: r.sub, key: r.key}))
  const isRowItself = row.cat === 'General' && row.sub === 'Uncategorised'
  const hasGeneral = others.some((o) => o.cat === 'General' && o.sub === 'Uncategorised')
  if (!hasGeneral && !isRowItself) {
    others.push({cat: 'General', sub: 'Uncategorised', key: 'General' + ARROW + 'Uncategorised'})
  }
  return others
}

/**
 * Mirrors useFreezeStore.js's own REMOVE_SUBCAT reducer exactly: it drops
 * `cat` from `subcats` only once ITS LISTED array is down to nothing, not
 * whenever `rows` (listed + carried-by-SKUs, merged) has no other entry for
 * it. An unlisted row never triggers this — there was nothing of its in
 * `subcats` to begin with.
 */
export function categoryHasNoOtherSubcategory(subcats, row) {
  if (!row || !row.listed) return false
  return ((subcats && subcats[row.cat]) || []).length === 1
}

/**
 * The one drawer behind all three sub-category mutations — add, rename,
 * remove. `rows` is the FULL (unfiltered, unpaginated) row list from
 * `SubcategoryPanel`, used for the clash-checks and the removal
 * destination list, both of which must see every sub-category, not just
 * whatever the panel's own search/page happens to show. `state` is passed
 * alongside `rows` because one check below — whether a category has any
 * OTHER sub-category left — has to read `state.subcats[cat]` directly (the
 * LISTED array `useFreezeStore.js`'s own REMOVE_SUBCAT reducer deletes the
 * category from), which `rows` alone (listed + carried-by-SKUs, merged)
 * cannot answer on its own.
 *
 * Keyboard-dismissable and focus-return are handled by MUI's underlying
 * Modal (Escape key, backdrop click, restore focus to the trigger) exactly
 * like MergeDrawer/ReviewSkuDrawer; `SubcategoryPanel`'s own `closeDrawer`
 * additionally falls back to focusing the active rail item when that
 * restore lands on `<body>` — the SAME `focusFallbackRef` mechanism those
 * two drawers already use, reused rather than invented a second time here.
 */
export default function SubcategoryDrawer({open, mode, row, rows, state, onClose, onAdd, onRename, onRemove}) {
  const categories = React.useMemo(() => Array.from(new Set((rows || []).map((r) => r.cat))).sort(), [rows])

  // add-mode fields
  const [cat, setCat] = React.useState('')
  const [newCatName, setNewCatName] = React.useState('')
  const [subName, setSubName] = React.useState('')
  // rename-mode field
  const [newName, setNewName] = React.useState('')
  // remove-mode field
  const [destKey, setDestKey] = React.useState('')

  // Reset every field to this mode/row's own starting point on the
  // closed -> open transition, not on every render — the same "adjusted
  // during render" pattern MergeDrawer/ReviewSkuDrawer use, so a stale pick
  // never carries over from the last time this one drawer instance was
  // shown for a different row or a different mode.
  const [prevOpen, setPrevOpen] = React.useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setCat('')
      setNewCatName('')
      setSubName('')
      setNewName(row ? row.sub : '')
      setDestKey('')
    }
  }

  const resolvedCat = resolveAddCategory(cat, newCatName)
  const trimmedSubName = subName.trim()
  const addClash = hasSubcatClash(rows, resolvedCat, trimmedSubName, null)
  const addReady = !!resolvedCat && trimmedSubName.length >= 2 && !addClash

  const trimmedNewName = newName.trim()
  const renameClash = !!row && hasSubcatClash(rows, row.cat, trimmedNewName, row.key)
  const renameReady = !!row && trimmedNewName.length >= 2 && !renameClash && trimmedNewName.toLowerCase() !== row.sub.toLowerCase()

  const destinationOptions = React.useMemo(() => buildDestinationOptions(rows, row), [rows, row])
  const chosenDestination = destinationOptions.find((d) => d.key === destKey) || null
  const removeReady = !!row && (row.count === 0 || !!chosenDestination)

  const catHasNoOtherSub = categoryHasNoOtherSubcategory(state.subcats, row)

  const ready = mode === 'add' ? addReady : mode === 'rename' ? renameReady : removeReady

  const handleSubmit = () => {
    if (mode === 'add') {
      if (!addReady) return
      onAdd(resolvedCat, trimmedSubName)
    } else if (mode === 'rename') {
      if (!renameReady || !row) return
      onRename(row.cat, row.sub, trimmedNewName)
    } else if (mode === 'remove') {
      if (!removeReady || !row) return
      onRemove(row.cat, row.sub, row.count === 0 ? null : {cat: chosenDestination.cat, sub: chosenDestination.sub})
    }
  }

  const requestTotal = row ? row.frozen + row.pending : 0

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 580}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                {TITLE[mode]}
              </Typography>
              {row && <Typography sx={labelSx}>{row.cat}{ARROW}{row.sub}</Typography>}
            </Box>
            <IconButton onClick={onClose} aria-label='Close sub-category drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
          {/* Verbatim business-rule copy, the same Alert PackingPage renders
              under its own header — rendered here at the top in all three
              modes. Do not paraphrase. */}
          <Alert severity='info' sx={alertSx}>{POLICY.subcatDefault}</Alert>

          {mode === 'add' && (
            <>
              <TextField
                select
                size='small'
                label='Category'
                value={cat}
                onChange={(e) => setCat(e.target.value)}
                sx={{fontFamily: FONT, fontSize: SIZE.xs}}
              >
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
                <MenuItem value='__new'>+ New category</MenuItem>
              </TextField>

              {cat === '__new' && (
                <TextField
                  size='small'
                  label='New category name'
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  sx={{fontFamily: FONT, fontSize: SIZE.xs}}
                />
              )}

              <TextField
                size='small'
                label='Sub-category name'
                value={subName}
                onChange={(e) => setSubName(e.target.value)}
                error={addClash}
                helperText={addClash ? 'That name already exists in this category.' : ' '}
                sx={{fontFamily: FONT, fontSize: SIZE.xs}}
              />
            </>
          )}

          {mode === 'rename' && row && (
            <>
              <Box sx={sectionCardSx}>
                <KeyValue items={[
                  {label: 'Category', value: row.cat},
                  {label: 'SKUs in this sub-category', value: int(row.count)},
                  {label: 'Shipments behind them', value: int(row.ships)},
                ]} />
              </Box>

              <TextField
                size='small'
                label='New name'
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                error={renameClash}
                helperText={renameClash ? 'That name already exists in this category.' : ' '}
                sx={{fontFamily: FONT, fontSize: SIZE.xs}}
              />

              {(row.frozen > 0 || row.pending > 0) && (
                <Alert severity='warning' sx={alertSx}>
                  Renaming carries those requests across to the new name, so the frozen weight keeps applying
                  to the same SKUs. The agreed weight itself does not change.
                </Alert>
              )}
            </>
          )}

          {mode === 'remove' && row && (
            <>
              {row.count === 0 ? (
                <Alert severity='info' sx={alertSx}>
                  Nothing sits in {row.sub}, so removing it moves no SKUs and changes no weights.
                </Alert>
              ) : (
                <>
                  <Alert severity='warning' sx={alertSx}>
                    {int(row.count)} {plural(row.count, 'SKU', 'SKUs')} ({int(row.ships)} shipments) currently sit in
                    this sub-category. They need somewhere to go before it can be removed.
                  </Alert>

                  <TextField
                    select
                    size='small'
                    label='Move those SKUs to'
                    value={destKey}
                    onChange={(e) => setDestKey(e.target.value)}
                    sx={{fontFamily: FONT, fontSize: SIZE.xs}}
                  >
                    {destinationOptions.map((d) => (
                      <MenuItem key={d.key} value={d.key}>{d.cat}{ARROW}{d.sub}</MenuItem>
                    ))}
                  </TextField>

                  <Box sx={sectionCardSx}>
                    <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 1}}>
                      What happens on save
                    </Typography>
                    <Box component='ul' sx={{m: 0, pl: 2.25, display: 'grid', gap: 0.5}}>
                      <Typography component='li' sx={bulletSx}>
                        {row.cat}{ARROW}{row.sub} comes off the list.
                      </Typography>
                      <Typography component='li' sx={bulletSx}>
                        {int(row.count)} {plural(row.count, 'SKU moves', 'SKUs move')} to{' '}
                        {chosenDestination ? `${chosenDestination.cat}${ARROW}${chosenDestination.sub}` : 'the destination chosen above'}.
                      </Typography>
                      <Typography component='li' sx={bulletSx}>
                        Dead weight, volumetric weight and packing material stay exactly as they are on each SKU.
                      </Typography>
                      {catHasNoOtherSub && (
                        <Typography component='li' sx={bulletSx}>
                          {row.cat} has no other sub-category, so the category goes too.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              {requestTotal > 0 && (
                <Alert severity='warning' sx={alertSx}>
                  {int(requestTotal)} freeze {plural(requestTotal, 'request', 'requests')} against this sub-category
                  {' '}{plural(requestTotal, 'has', 'have')} nothing left to apply to once it is removed.
                </Alert>
              )}

              {!row.listed && (
                <Alert severity='info' sx={alertSx}>
                  This pair is not on the official sub-category list — it exists here only because SKUs carry it.
                  Removing it only clears their sub-category; there is nothing to delete from the list itself.
                </Alert>
              )}
            </>
          )}
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          <Button sx={mode === 'remove' ? dangerBtnSx : primaryBtnSx} disabled={!ready} onClick={handleSubmit}>
            {mode === 'add' && 'Add sub-category'}
            {mode === 'rename' && 'Save new name'}
            {mode === 'remove' && (row && row.count > 0 ? `Remove and move ${int(row.count)} SKUs` : 'Remove sub-category')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}
